// Surfaces the backend's outstanding-invoice message so the UI can show the
// real reason ("This account has an unpaid invoice…") instead of a generic
// "try again" toast — but only when we can actually determine that's the cause.

// Both unpaid-invoice guards ("This account has an unpaid invoice…", "Credit
// purchases are unavailable while this account has an unpaid invoice.") contain
// this phrase.
const OUTSTANDING_INVOICE_PATTERN = /unpaid invoice/i

interface ApiErrorResponse {
  type: string
  status?: number
  data?: unknown
}

// Thrown only when we have determined the failure is the outstanding-invoice
// guard. Callers surface its message to the user; any other thrown error keeps
// the generic toast, so unrelated messages ("No workspace found", etc.) never
// leak into the UI.
export class OutstandingInvoiceError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'OutstandingInvoiceError'
  }
}

const toMessageList = (errors: unknown): unknown[] => {
  if (Array.isArray(errors)) return errors
  if (typeof errors === 'string') return [errors]
  return []
}

// Returns the server's message ONLY when we can determine the failure is the
// outstanding-invoice guard: a client error (4xx) whose { errors: [...] } names
// an unpaid invoice. 5xx / exceptions carry no reliable reason, and non-invoice
// failures are left to the caller's generic fallback — so we never guess.
export const outstandingInvoiceMessage = (response: ApiErrorResponse): string | null => {
  if (response.type !== 'error') return null
  if (typeof response.status !== 'number' || response.status < 400 || response.status >= 500) return null

  const errors = (response.data as { errors?: unknown } | null)?.errors
  const messages = toMessageList(errors)

  const match = messages.find(
    (message): message is string => typeof message === 'string' && OUTSTANDING_INVOICE_PATTERN.test(message),
  )
  return match ?? null
}
